
import { KulionerController } from "@/app/ui/controllers/KulionerController";
import { KulionerListRequest } from "@/data/payload/api/KulionerApiRequest";
import { Options, Vue } from "vue-class-component";
import { KulionerMerchant, KulionerOrder } from "@/domain/entities/Kulioner";
import CardListKulionerMerchant from "../components/card-list-kulioner-merchant.vue";
import ModalProcessOrder from "../components/modal-process-order.vue";
import FooterListKulioner from "../components/FooterListKulioner.vue";
import SummaryCard from "../components/summary-card.vue";
import { computeKulionerTotals } from "@/app/ui/views/kulioner/utils/totals";
import formatPrice from "@/app/infrastructures/misc/common-library/v2/FormatPrice";

interface Tab {
  label: string;
  count: number;
  routeName: string;
  status: string;
}

@Options({
  components: {
    CardListKulionerMerchant,
    ModalProcessOrder,
    FooterListKulioner,
    SummaryCard,
  }
})
class MerchantTabProcess extends Vue {
  isLoadingMerchants = true;
  merchantsOld: KulionerMerchant[] = [];
  merchantsTransaction: KulionerMerchant[] = [];
  merchants: KulionerMerchant[] = [];
  orderIds: KulionerOrder[] = [];
  eligibleOrderIds: KulionerOrder[] = [];
  canBeProcessed = false;

  mounted() {
    this.getOrderList();
  }

  tabs: Tab[] = [
    {
      label: this.$t("kulioner.merchant.tabs.processed") as string,
      count: 0,
      routeName: "kulioner-merchant-tab-cycle-processed",
      status: "ORDPRC"
    },
    {
      label: this.$t("kulioner.merchant.tabs.unprocessed") as string,
      count: 0,
      routeName: "kulioner-merchant-tab-cycle-unprocessed",
      status: "ORDWTC"
    }
  ];

  isActiveCycle(name: string) {
    return String(this.$route.name) === String(name);
  }

  get cycleIds() {
    return KulionerController.cycles
      .find((cycle) => {
        return cycle.cutOffTime === (this.$route.params.id as string);
      })
      ?.id.join(",");
  }

  get totalMerchantCompleted() {
    return this.computedTotals.totalMerchantCompleted;
  }

  get totalMerchant() {
    return this.computedTotals.totalMerchant;
  }

  get totalProduct() {
    return this.computedTotals.totalProduct;
  }

  get totalProductCompleted() {
    return this.computedTotals.totalProductCompleted;
  }

  get totalPieces() {
    return this.computedTotals.totalPieces;
  }

  get totalActualPieces() {
    return this.computedTotals.totalActualPieces;
  }

  // price multiple by quantity
  get totalTransaction() {
    return formatPrice(this.computedTotals.totalTransaction);
  }

  // price multiply by actual quantity
  get totalTransactionCompleted() {
    return formatPrice(
      computeKulionerTotals(this.merchantsTransaction).totalTransactionCompleted
    );
  }

  // Aggregate totals once for re-use across getters
  get computedTotals() {
    return computeKulionerTotals(this.merchantsOld);
  }

  get status() {
    return (this.$route.name as string).includes("unprocessed")
      ? "ORDWTC"
      : "ORDPRC";
  }

  async getOrderList() {
    await this.$nextTick();
    try {
      this.isLoadingMerchants = true;
      await this.getOrderSummary();
      const resp = await KulionerController.getKulionerMerchantList(
        new KulionerListRequest({
          viewAs: "MERCHANT",
          cycleId: this.cycleIds,
          status: this.status
        })
      );
      this.merchants = resp.data.orders.map((merchant) => {
        return new KulionerMerchant({
          ...merchant,
          products: merchant.products
            .sort((a, b) => (a === b ? -1 : 1))
            .map((product) => ({
              ...product,
              actualQuantity: product.isQuantityUpdated
                ? product.actualQuantity
                : product.quantity
            }))
        });
      });
      this.merchantsTransaction = this.merchants;
      this.merchantsOld = resp.data.orders;
      this.orderIds = resp.data.orderIds.map(
        (id) =>
          new KulionerOrder({
            orderShipmentId: id
          })
      );
      this.eligibleOrderIds = resp.data.eligibleOrderIds.map(
        (id) =>
          new KulionerOrder({
            orderShipmentId: id
          })
      );
      this.canBeProcessed = resp.data.canBeProcessed;
    } catch (error) {
      console.error("Error fetching order list:", error);
    } finally {
      this.isLoadingMerchants = false;
    }
  }

  async getOrderSummary() {
    const resp = await KulionerController.getListSummaryStatus(
      new KulionerListRequest({
        viewAs: "MERCHANT",
        cycleId: this.cycleIds
      })
    );
    this.tabs = this.tabs.map((tab) => {
      return {
        ...tab,
        count: resp[tab.status] || 0
      };
    });
  }

  updateModelValue(data: KulionerMerchant[]) {
    this.merchants = data;
  }
}
export default MerchantTabProcess;
